import React from 'react';
import { MetricCard, Card, CardTitle, BarRow, Grid, Insight } from '../components/UI';
import { usePassesStats, useRevenueStats, useStoreBillingStats } from '../hooks/useAnalytics';
import { MONTHLY_PRICE } from '../constants/pricing';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
function fmt(n) { return n === null ? '\u2026' : Number(n).toLocaleString(); }
function fmtUSD(n) { return n === null || n === undefined ? '\u2026' : '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }); }
function pct(a, b) { return b ? Math.round((a / b) * 100) : 0; }

function Skel({ h = 12, mb = 10 }) {
  return (
    <div style={{ height: h, borderRadius: 6, marginBottom: mb,
      background: 'linear-gradient(90deg,#edf2f5 25%,#f5f7f9 50%,#edf2f5 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.6s ease infinite' }} />
  );
}

function StatRow({ label, value, sub, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '9px 0', borderBottom: '.5px solid rgba(49,79,92,.05)' }}>
      <div>
        <div style={{ fontSize: '.73rem', color: '#4a6878' }}>{label}</div>
        {sub && <div style={{ fontSize: '.6rem', color: '#9eb8c8', marginTop: 1 }}>{sub}</div>}
      </div>
      <span style={{ fontSize: '.78rem', fontWeight: 600, color: color || '#0f1e28' }}>{value}</span>
    </div>
  );
}

export default function Revenue({ onNav }) {
  const { data: passes, loading: pL } = usePassesStats();
  const { data: rev,    loading: rL } = useRevenueStats();
  const { data: store, loading: storeLoading } = useStoreBillingStats();

  const loading = pL || rL;
  const totalRevenue = rev?.totalRev || 0;
  const rev30        = rev?.rev30 || 0;
  const rev7         = rev?.rev7  || 0;
  const arpu         = rev?.arpu  || '0.00';
  const byType       = rev?.byType || [];
  const maxRev       = byType[0]?.revenue || 1;

  // ── New MONTHLY LTV / recurring fields (May 2026) ──
  const oneTimeRev30    = rev?.oneTimeRev30    || 0;
  const recurringRev30  = rev?.recurringRev30  || 0;
  const recurringMrr    = rev?.recurringMrr    || 0;
  const monthlyActive   = rev?.monthlyActiveUsers || 0;
  const monthlyLtvAvg   = rev?.monthlyLtvAvg   || '0.00';
  const monthlyChurnPct = rev?.monthlyChurnPct || 0;
  const trialConvPct    = rev?.trialConversionPct || 0;
  const trialCount      = rev?.trialCount      || 0;
  const trialConverted  = rev?.trialConverted  || 0;

  // -- Store billing & sources (added Jun 2026) --
  const STORE_LABEL = { PLAY_STORE: 'Google Play', APP_STORE: 'Apple', STRIPE: 'Web \u00b7 Stripe' };
  const byStore       = store?.by_store || [];
  const mrrStore      = store?.mrr_by_store || [];
  const recon         = store?.reconciliation || {};
  const recentStore   = store?.recent || [];
  const sot           = store?.source_of_truth || {};
  const maxStoreSubs  = Math.max(1, ...byStore.map(s => s.subs || 0));
  const totalStoreMrr = mrrStore.reduce((a, m) => a + (m.mrr || 0), 0);
  const hasStore      = byStore.length > 0 || recentStore.length > 0;

  return (
    <div style={{ fontFamily: F }}>
      <style>{'@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}'}</style>

      {/* ── KPI row ──────────────────────────────────────────────────────────── */}
      <Grid cols={4}>
        <MetricCard
          label="Total revenue (all time)"
          value={loading ? '\u2026' : fmtUSD(totalRevenue)}
          sub={!loading && rev?.total ? fmt(rev.total) + ' purchases' : undefined}
          trend={totalRevenue > 0 ? 'up' : null}
          accent={totalRevenue > 0 ? '#2da06a' : '#9eb8c8'}
        />
        <MetricCard
          label="Last 30 days"
          value={loading ? '\u2026' : fmtUSD(rev30)}
          sub={!loading && rev7 > 0 ? fmtUSD(rev7) + ' last 7d' : undefined}
          accent="#314F5C"
        />
        <MetricCard
          label="ARPU"
          value={loading ? '\u2026' : '$' + arpu}
          sub="avg revenue per user"
          accent="#6F8FA7"
        />
        <MetricCard
          label="Refund requests"
          value={loading ? '\u2026' : fmt(rev?.refunds || 0)}
          sub={!loading && rev?.total > 0 ? pct(rev.refunds, rev.total) + '% of purchases' : undefined}
          trend={rev?.refunds > 0 ? 'down' : null}
          accent={rev?.refunds > 0 ? '#e05c5c' : '#2da06a'}
        />
      </Grid>

      {/* ── MONTHLY-subscription recurring/LTV row (added May 2026) ────────── */}
      <Grid cols={4}>
        <MetricCard
          label="MRR (recurring)"
          value={loading ? '\u2026' : fmtUSD(recurringMrr)}
          sub={!loading ? fmt(monthlyActive) + ' active MONTHLY subs · $' + MONTHLY_PRICE + '/mo list' : undefined}
          trend={recurringMrr > 0 ? 'up' : null}
          accent="#9F7E4F"
        />
        <MetricCard
          label="Annual rev (30d)"
          value={loading ? '\u2026' : fmtUSD(oneTimeRev30)}
          sub={!loading ? fmtUSD(recurringRev30) + ' recurring (30d)' : undefined}
          accent="#314F5C"
        />
        <MetricCard
          label="MONTHLY LTV avg"
          value={loading ? '\u2026' : '$' + monthlyLtvAvg}
          sub={'months_active × $' + MONTHLY_PRICE + ' list price per MONTHLY user'}
          accent="#6F8FA7"
        />
        <MetricCard
          label="MONTHLY churn (30d)"
          value={loading ? '\u2026' : monthlyChurnPct + '%'}
          sub={!loading ? 'Trial → paid: ' + trialConvPct + '% (' + fmt(trialConverted) + '/' + fmt(trialCount) + ')' : undefined}
          trend={monthlyChurnPct > 10 ? 'down' : monthlyChurnPct > 0 ? null : null}
          accent={monthlyChurnPct > 10 ? '#e05c5c' : '#2da06a'}
        />
      </Grid>

      {totalRevenue === 0 && !loading ? (
        <Card>
          <Insight type="info">
            No purchases recorded yet. Revenue data will appear here once users buy passes.
            Pass types (7-day, 14-day, 30-day, Annual) are configured in the app.
          </Insight>
        </Card>
      ) : (
        <Grid cols={2} mb={0}>

          {/* ── Revenue by pass type ──────────────────────────────────────── */}
          <Card style={{ marginBottom: 0 }}>
            <CardTitle>Revenue by pass type</CardTitle>
            {loading ? [1,2,3,4].map(i => <Skel key={i} mb={10} />) :
              byType.length === 0 ? (
                <Insight type="info">No revenue data yet.</Insight>
              ) : (
                byType.map(({ type, label, count, revenue }) => (
                  <BarRow
                    key={type}
                    label={label || type.replace('PASS_', '')}
                    value={fmtUSD(revenue)}
                    pct={pct(revenue, maxRev)}
                    labelWidth={80}
                  />
                ))
              )
            }
          </Card>

          {/* ── Revenue stats + recent purchases ─────────────────────────── */}
          <div>
            <Card>
              <CardTitle>Summary</CardTitle>
              <StatRow label="Total revenue"   value={fmtUSD(totalRevenue)} color="#2da06a" />
              <StatRow label="Last 30 days"    value={fmtUSD(rev30)} />
              <StatRow label="Last 7 days"     value={fmtUSD(rev7)} />
              <StatRow label="Unique buyers"   value={fmt(rev?.uniqueUsers || 0)} />
              <StatRow label="ARPU"            value={'$' + arpu} sub="avg revenue per paying user" />
              <StatRow label="Refunds"         value={fmt(rev?.refunds || 0)}
                color={rev?.refunds > 0 ? '#e05c5c' : '#2da06a'} />
            </Card>

            <Card style={{ marginBottom: 0 }}>
              <CardTitle>Pass count by type</CardTitle>
              {loading ? [1,2,3,4].map(i => <Skel key={i} mb={10} />) :
                byType.map(({ type, label, count }) => (
                  <BarRow
                    key={type}
                    label={label || type.replace('PASS_', '')}
                    value={fmt(count)}
                    pct={pct(count, byType[0]?.count || 1)}
                    labelWidth={80}
                  />
                ))
              }
            </Card>
          </div>
        </Grid>
      )}

      {/* ── Recent purchases ─────────────────────────────────────────────────── */}
      {!loading && (rev?.recent || []).length > 0 && (
        <Card style={{ marginTop: 14 }}>
          <CardTitle>Recent purchases</CardTitle>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr',
            gap: 8, padding: '6px 0', borderBottom: '1px solid #e9eef2',
            fontSize: '.6rem', fontWeight: 600, color: '#9eb8c8',
            textTransform: 'uppercase', letterSpacing: '.06em' }}>
            <div>Email</div><div>Pass</div><div>Price</div><div>Date</div>
          </div>
          {(rev.recent || []).slice(0, 8).map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr',
              gap: 8, padding: '8px 0', borderBottom: '.5px solid rgba(49,79,92,.04)',
              fontSize: '.7rem', alignItems: 'center' }}>
              <div style={{ color: '#0f1e28', overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap' }}>{r.email || 'Anonymous'}</div>
              <div style={{ color: '#314F5C', fontWeight: 500 }}>
                {(r.pass_label || r.pass_type || '').replace('PASS_', '')}
              </div>
              <div style={{ color: '#2da06a', fontWeight: 600 }}>{fmtUSD(r.price_usd)}</div>
              <div style={{ color: '#9eb8c8' }}>
                {r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short' }) : '\u2014'}
              </div>
            </div>
          ))}
        </Card>
      )}
      {/* -- Store billing & source reconciliation (added Jun 2026) -- */}
      <Grid cols={2} mb={0}>
        <Card style={{ marginTop: 14, marginBottom: 0 }}>
          <CardTitle>Subscriptions by source</CardTitle>
          {storeLoading ? [1,2,3].map(i => <Skel key={i} />) :
            !hasStore ? (
              <Insight type="info">No store purchases yet. Fills in once the RevenueCat webhook and native validators start writing events.</Insight>
            ) : (
              byStore.map(s => (
                <BarRow key={s.store} label={STORE_LABEL[s.store] || s.store}
                  value={fmt(s.subs)} pct={pct(s.subs, maxStoreSubs)} labelWidth={92} />
              ))
            )
          }
        </Card>
        <Card style={{ marginTop: 14, marginBottom: 0 }}>
          <CardTitle>Recurring MRR by source</CardTitle>
          {storeLoading ? [1,2,3].map(i => <Skel key={i} />) :
            mrrStore.length === 0 ? (
              <Insight type="info">No recurring store revenue yet.</Insight>
            ) : (
              <>
                {mrrStore.map(m => (
                  <StatRow key={m.store} label={STORE_LABEL[m.store] || m.store} value={fmtUSD(m.mrr)} />
                ))}
                <StatRow label="Total store MRR" value={fmtUSD(totalStoreMrr)} color="#2da06a" />
              </>
            )
          }
        </Card>
      </Grid>

      {/* -- Billing source reconciliation (RevenueCat vs native) -- */}
      <Card style={{ marginTop: 14 }}>
        <CardTitle>Billing source reconciliation</CardTitle>
        <StatRow label="Events compared (30d)" value={fmt(recon.compared || 0)} />
        <StatRow label="Native agreement with RevenueCat"
          value={recon.agreement_pct == null ? '\u2014' : recon.agreement_pct + '%'}
          color={recon.agreement_pct == null ? '#9eb8c8' : recon.agreement_pct >= 99 ? '#2da06a' : '#9F7E4F'} />
        <StatRow label="Discrepancies to review" value={fmt(recon.discrepancies || 0)}
          color={(recon.discrepancies || 0) > 0 ? '#9F7E4F' : '#2da06a'} />
        <StatRow label="Source of truth · Play Store" value={sot.PLAY_STORE || 'revenuecat'} />
        <Insight type="info">Native validators run in shadow. When agreement holds near 100%, flip iap_source_of_truth to google_native to drop the RevenueCat fee.</Insight>
      </Card>

      {recentStore.length > 0 && (
        <Card style={{ marginTop: 14 }}>
          <CardTitle>Recent store events</CardTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '92px 2fr 1fr 0.6fr',
            gap: 8, padding: '6px 0', borderBottom: '1px solid #e9eef2',
            fontSize: '.6rem', fontWeight: 600, color: '#9eb8c8',
            textTransform: 'uppercase', letterSpacing: '.06em' }}>
            <div>Source</div><div>Event</div><div>User</div><div>Date</div>
          </div>
          {recentStore.slice(0, 8).map((e, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '92px 2fr 1fr 0.6fr',
              gap: 8, padding: '8px 0', borderBottom: '.5px solid rgba(49,79,92,.04)',
              fontSize: '.7rem', alignItems: 'center' }}>
              <div style={{ color: '#314F5C', fontWeight: 500 }}>{e.source}</div>
              <div style={{ color: '#0f1e28' }}>{(e.event_type || '') + (e.plan_key ? ' \u00b7 ' + e.plan_key : '')}</div>
              <div style={{ color: '#6F8FA7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {e.app_user_id ? String(e.app_user_id).slice(0, 8) + '\u2026' : '\u2014'}</div>
              <div style={{ color: '#9eb8c8' }}>
                {e.created_at ? new Date(e.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '\u2014'}</div>
            </div>
          ))}
        </Card>
      )}

    </div>
  );
}
